import {
  MediaController,
  MediaControlBar,
  MediaTimeRange,
  MediaTimeDisplay,
  MediaVolumeRange,
  MediaPlaybackRateButton,
  MediaPlayButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaMuteButton,
  MediaFullscreenButton,
  MediaPreviewThumbnail,
  MediaPipButton,
} from "media-chrome/react";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { Button, Card, CardBody, Collapse, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";

type TProps = {
  src: string;
  title?: string;
  subTitle?: string;
  content?: string;
  /** Keep approximate playback position when switching sources (e.g. original ↔ converted). */
  preservePosition?: boolean;
};

const isHlsUrl = (url?: string) => !!url && /\.m3u8(\?|$)/i.test(url);

const withCdnCredentials = (xhr: XMLHttpRequest) => {
  xhr.withCredentials = true;
};

export const AppVideoPlayer: FC<TProps> = ({
  src,
  title,
  subTitle,
  content,
  preservePosition = true,
}) => {
  const { t } = useTranslation();
  const playerRef = useRef<HTMLVideoElement | null>(null);
  // Currently mounted source — kept until the next one is ready so the frame doesn't blank.
  const [activeSrc, setActiveSrc] = useState<string>();
  const [isPreparing, setIsPreparing] = useState(false);
  const [isOpenVideoContent, setIsOpenVideoContent] = useState(false);
  const resumeAtRef = useRef(0);
  const prepareIdRef = useRef(0);

  const setPlayerRef = useCallback((player: HTMLVideoElement) => {
    if (!player) return;
    playerRef.current = player;
  }, []);

  const toggle = () => setIsOpenVideoContent(!isOpenVideoContent);

  const applyResumePosition = useCallback(() => {
    const el = playerRef.current;
    const resumeAt = resumeAtRef.current;
    if (!el || resumeAt <= 0) return;

    const seek = () => {
      try {
        if (Number.isFinite(el.duration) && el.duration > 0) {
          el.currentTime = Math.min(resumeAt, Math.max(el.duration - 0.25, 0));
        } else {
          el.currentTime = resumeAt;
        }
      } catch {
        // Some streams reject seek before enough data; ignore.
      }
      resumeAtRef.current = 0;
      void el.play?.().catch(() => undefined);
    };

    if (el.readyState >= 1) {
      seek();
    } else {
      el.addEventListener("loadedmetadata", seek, { once: true });
    }
  }, []);

  // Prepare the next source without tearing down the current one until ready.
  useEffect(() => {
    if (!src) {
      setActiveSrc(undefined);
      setIsPreparing(false);
      return;
    }

    if (src === activeSrc) {
      setIsPreparing(false);
      return;
    }

    const prepareId = ++prepareIdRef.current;
    setIsPreparing(true);

    if (preservePosition && playerRef.current) {
      const currentTime = playerRef.current.currentTime;
      if (Number.isFinite(currentTime) && currentTime > 1) {
        resumeAtRef.current = currentTime;
      }
    } else {
      resumeAtRef.current = 0;
    }

    let cancelled = false;

    const commit = (nextSrc: string) => {
      if (cancelled || prepareId !== prepareIdRef.current) return;
      setActiveSrc(nextSrc);
    };

    const prepare = async () => {
      if (!isHlsUrl(src)) {
        commit(src);
        return;
      }

      // Warm CDN cookies / confirm playlist access before mounting HLS.
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const res = await fetch(src, { credentials: "include" });
          if (!res.ok) throw new Error(`playlist ${res.status}`);
          await res.text();
          if (cancelled || prepareId !== prepareIdRef.current) return;
          commit(src);
          return;
        } catch {
          if (cancelled || prepareId !== prepareIdRef.current) return;
          if (attempt < 3) {
            await new Promise((r) => setTimeout(r, 250 * attempt));
          }
        }
      }

      // Last resort: still try to mount; player may recover with cookies already set.
      commit(src);
    };

    void prepare();

    return () => {
      cancelled = true;
    };
    // activeSrc intentionally omitted — we only react to external src changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, preservePosition]);

  const isHlsSrc = isHlsUrl(activeSrc);
  const showShell = !!src || !!activeSrc;

  return (
    <React.Fragment>
      <div className="mb-3 app-video-player" dir="ltr">
        {showShell && (
          <div className="app-video-player__stage">
            {(isPreparing || !activeSrc) && (
              <div className="app-video-player__overlay" aria-busy="true">
                <Spinner color="light" size="sm" />
                <span className="app-video-player__overlay-text">
                  {t("loading", { defaultValue: "Loading…" })}
                </span>
              </div>
            )}

            {activeSrc && (
              <MediaController className="app-video-player__controller">
                <ReactPlayer
                  config={
                    isHlsSrc
                      ? {
                          hls: {
                            xhrSetup: withCdnCredentials,
                            fetchSetup: (
                              context: { url: string },
                              init: RequestInit,
                            ) =>
                              new Request(context.url, {
                                ...init,
                                credentials: "include",
                              }),
                          },
                        }
                      : undefined
                  }
                  slot="media"
                  key={activeSrc}
                  ref={setPlayerRef}
                  className="react-player"
                  autoPlay
                  playsInline
                  playIcon
                  style={{
                    width: "100%",
                    height: "auto",
                    aspectRatio: "16/9",
                  }}
                  src={activeSrc}
                  controls={false}
                  preload="auto"
                  crossOrigin={isHlsSrc ? "use-credentials" : undefined}
                  onReady={() => {
                    applyResumePosition();
                    setIsPreparing(false);
                  }}
                  onPlaying={() => setIsPreparing(false)}
                />
                <MediaControlBar style={{ backgroundColor: "#5686c8" }}>
                  <MediaPlayButton />
                  <MediaSeekBackwardButton seekOffset={10} />
                  <MediaSeekForwardButton seekOffset={10} />
                  <MediaTimeRange />
                  <MediaTimeDisplay showDuration remaining />
                  <MediaMuteButton />
                  <MediaVolumeRange />
                  <MediaPlaybackRateButton />
                  <MediaFullscreenButton />
                  <MediaPreviewThumbnail />
                  <MediaPipButton />
                </MediaControlBar>
              </MediaController>
            )}
          </div>
        )}
      </div>
      {title && <h5 className="fw-bold text-start text-light">{title}</h5>}
      {subTitle && <h6 className="small text-start text-light">{subTitle}</h6>}
      {content && (
        <div className="my-3">
          <Button
            tag={"a"}
            color="primary"
            className="text-light btn-sm"
            onClick={toggle}
            style={{ marginBottom: "1rem" }}
          >
            {isOpenVideoContent ? t("Hide Detail") : t("Show Detail")}
          </Button>
          <Collapse isOpen={isOpenVideoContent}>
            <Card>
              <CardBody>
                <div
                  className="text-light text-start"
                  dangerouslySetInnerHTML={{
                    __html: content || "",
                  }}
                ></div>
              </CardBody>
            </Card>
          </Collapse>
        </div>
      )}
    </React.Fragment>
  );
};
